
import { Component, Vue } from "vue-property-decorator";
import { SMCases, SMUser } from "@/store";
import { API_ROUTES } from "@/utils/API";

import {
  IN_PROGRESS_OA_STATUS,
  READY_TO_SUBMIT_OA_STATUS,
  ON_REVIEW_OA_STATUS,
  SKILLED_WORKER_SERVICE_TYPE,
  SKILLED_WORKER_INSIDE_SERVICE_TYPE,
  SPONSOR_LICENCE_SERVICE_TYPE,
  SKILLED_WORKER_COS_SERVICE_TYPE,
  ADDITIONAL_DATA_AND_DOCUMENTS_SERVICE_STEP,
  SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
  DEPENDANT_INSIDE_SERVICE_TYPE,
} from "@/constants/services";

import { SUCCESS_POPUP_TYPE } from "@/constants/popupTypes";
import { API_TCaseOnlineApplication } from "@/models/case_online_application";
import { TTask_Table } from "@/models/tasks_services";
import { TO_DO_TASK_STATUS } from "@/constants/tasks";

Component.registerHooks(["beforeRouteEnter", "beforeRouteUpdate"]);

const CANDIDATE_DASHBOARD_ROUTE = "candidateDashboard";

const COL_SPAN_CLASS = "col-span";

const COS_TAB_NAME = "cos";
const FINAL_APPLICATION_NAME = "final_application";

const EMIT_MOVE_TO_TASKS_TABLE_TIMEOUT = 3000;
const FORMBOT_PAGE_READY_EVENT = "formbot:page-ready";

@Component({
  components: {
    Loader: () => import("@/components/Loader/Loader.vue"),
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
    OnlineApplication: () =>
      import(
        "@/modules/Services/Template/views/OnlineApplication/TemplateOnlineApplication/OnlineApplication.vue"
      ),
    AdvisorNotes: () => import("@/components/Widgets/Expert/AdvisorNotes.vue"),
    NotificationDataNotLoaded: () =>
      import("@/components/Notifications/NotificationDataNotLoaded.vue"),
    OnlineApplicationNotification: () =>
      import(
        "@/modules/Services/Template/views/OnlineApplication/TemplateOnlineApplication/OnlineApplicationNotification.vue"
      ),
  },
})
export default class VTemplateOnlineApplication extends Vue {
  successPopup = false;

  successType = SUCCESS_POPUP_TYPE;

  columns = 12;

  notesActive = false;

  get onlineApplicationStatus() {
    return SMCases.onlineApplicationStatus;
  }

  get isSkilledWorkerCase(): boolean {
    return SMCases.caseType === SKILLED_WORKER_SERVICE_TYPE;
  }

  get isSkilledWorkerInsideCase(): boolean {
    return SMCases.caseType === SKILLED_WORKER_INSIDE_SERVICE_TYPE;
  }

  get isCosSwCase(): boolean {
    return SMCases.caseType === SKILLED_WORKER_COS_SERVICE_TYPE;
  }

  get isCosSwInsideCase(): boolean {
    return SMCases.caseType === SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE;
  }

  get isSponsorLicenceCase(): boolean {
    return SMCases.caseType === SPONSOR_LICENCE_SERVICE_TYPE;
  }

  get isDependantCase(): boolean {
    return [DEPENDANT_INSIDE_SERVICE_TYPE].includes(SMCases.caseType);
  }

  get isFinalApplicationTab(): boolean {
    return this.$route.query.name === FINAL_APPLICATION_NAME;
  }

  get isCosTab(): boolean {
    return this.$route.query.name === COS_TAB_NAME;
  }

  get isRelevantCaseType() {
    return (
      this.isSponsorLicenceCase ||
      this.isSkilledWorkerCase ||
      this.isSkilledWorkerInsideCase ||
      this.isCosSwCase ||
      this.isCosSwInsideCase ||
      this.isDependantCase
    );
  }

  get showMoveToTaskTableButton(): boolean {
    return !!(this.isFinalApplicationTab && this.isRelevantCaseType && this.tasks.length);
  }

  showNotes(isActive: boolean) {
    this.notesActive = isActive;
    this.columns = isActive ? 8 : 12;
  }

  get classes(): string {
    return `${COL_SPAN_CLASS}-${this.columns}`;
  }

  get isExpert(): boolean {
    return SMUser.isExpert;
  }

  service: API_TCaseOnlineApplication | string = "";

  get serviceId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  get tabTypeId(): number {
    return Number(this.$route.params.tabTypeId ?? 0);
  }

  get inProgress() {
    return (
      SMCases.onlineApplicationStatus === IN_PROGRESS_OA_STATUS &&
      ![ADDITIONAL_DATA_AND_DOCUMENTS_SERVICE_STEP].includes(SMCases.caseStepName)
    );
  }

  get readyToSubmit() {
    return SMCases.onlineApplicationStatus === READY_TO_SUBMIT_OA_STATUS;
  }

  openSuccessPopup() {
    this.successPopup = true;
  }

  closeSuccessPopup() {
    this.successPopup = false;

    this.$router.push({
      name: CANDIDATE_DASHBOARD_ROUTE,
    });
  }

  get isSubmitVisible() {
    if (this.inProgress) return false;

    const isSwService = [SKILLED_WORKER_SERVICE_TYPE, SKILLED_WORKER_INSIDE_SERVICE_TYPE].includes(
      SMCases.caseType
    );

    return !(SMUser.isEmployer && isSwService) && this.$attrs?.showSubmitButton;
  }

  async submit() {
    try {
      this.submitLoading = true;

      await this.axios.get(API_ROUTES.CASE_SEND_TO_REWIEW(this.serviceId));

      this.openSuccessPopup();

      SMCases.setOnlineApplicationStatus(ON_REVIEW_OA_STATUS);
    } finally {
      this.submitLoading = false;
    }
  }

  loading = false;

  submitLoading = false;

  notifyFormbotPageReady() {
    this.$root.$emit(FORMBOT_PAGE_READY_EVENT);
  }

  async getServiceOnlineApplication() {
    if (this.inProgress && !this.isExpert) {
      this.notifyFormbotPageReady();
      return;
    }

    if (this.loading) return;

    const { serviceId, tabTypeId } = this;

    try {
      this.loading = true;

      const { data } = await this.axios.get<API_TCaseOnlineApplication>(
        API_ROUTES.CASE_ONLINE_APPLICATION(serviceId, tabTypeId)
      );

      if (!data) return;

      this.service = data;
    } finally {
      this.loading = false;
      this.notifyFormbotPageReady();
    }
  }

  get caseId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  tasks = [];

  async getTasks() {
    const { data } = await this.axios.get(API_ROUTES.CASE_TASKS(this.caseId));

    if (!data) return;

    this.tasks = data.filter((item: TTask_Table) => item.status === TO_DO_TASK_STATUS);
  }

  checkForTasks() {
    if (
      !this.isSponsorLicenceCase &&
      !this.isSkilledWorkerCase &&
      !this.isSkilledWorkerInsideCase &&
      !(this.isCosSwCase || this.isCosSwInsideCase) &&
      !this.isDependantCase
    )
      return;

    this.getTasks();
  }

  emitMoveToTable() {
    setTimeout(() => {
      this.$root.$emit("move-to-tasks-table");
    }, EMIT_MOVE_TO_TASKS_TABLE_TIMEOUT);
  }

  async moveToTasksTable() {
    await this.getTasks();

    if (!this.tasks.length) return;

    this.$router.push(
      {
        name: CANDIDATE_DASHBOARD_ROUTE,
        params: {
          moveToTasksTable: "true",
        },
      },
      this.emitMoveToTable
    );
  }

  async activated() {
    if (!this.caseId) return;

    this.loading = true;

    try {
      await SMCases.fetchCaseTabs(SMCases.caseId);
    } finally {
      this.loading = false;
    }

    await this.getServiceOnlineApplication();

    await this.checkForTasks();
  }
}
